import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Tab, TabList, Flex, Text, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'

const SearchCategoryTabs = ({
    productSearchResult,
    tabButtonLabels,
    faqArticlesHits,
    manualsHits,
    isSearch,
    selectedIndex,
}) => {
    const styles = useStyleConfig('PageHeader')
    const {formatMessage} = useIntl()
    const countPlaceholder = '__TAB_COUNT__'
    const tabListRef = useRef(null)
    const tabRefs = useRef({})
    const tabKeys = Object.keys(tabButtonLabels)

    const scrollTabIntoView = (key) => {
        const tab = tabRefs.current[key]
        const container = tabListRef.current
        if (!tab || !container) return

        const containerRect = container.getBoundingClientRect()
        const tabRect = tab.getBoundingClientRect()

        let offset = 0
        if (tabRect.left < containerRect.left) {
            offset = tabRect.left - containerRect.left
        } else if (tabRect.right > containerRect.right) {
            offset = tabRect.right - containerRect.right
        }

        if (offset !== 0) container.scrollBy({left: offset, behavior: 'smooth'})
    }

    useEffect(() => {
        const selectedKey = tabKeys[selectedIndex]
        if (selectedKey) scrollTabIntoView(selectedKey)
    }, [selectedIndex])

    const getTabTotal = (key) => {
        if (key === 'productResults') return productSearchResult?.total || 0
        if (key === 'manuals') return manualsHits || 0
        if (key === 'faq') return faqArticlesHits || 0
        return 0
    }

    const getTabLabel = (key) => {
        const rawLabel = formatMessage(tabButtonLabels[key], {total: countPlaceholder})
        return rawLabel
            .replace(/\s*\(\s*__TAB_COUNT__\s*\)\s*/g, ' ')
            .replace(countPlaceholder, '')
            .replace(/[()]/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim()
    }

    return (
        <TabList
            id="plp-search-tabs"
            ref={tabListRef}
            {...styles.searchContainerButtons}
            sx={{
                '::-webkit-scrollbar': {display: 'none'},
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                display: isSearch ? 'flex' : 'none',
            }}
        >
            {Object.keys(tabButtonLabels).map((key) => {
                const total = getTabTotal(key)
                const label = getTabLabel(key)

                return (
                    <Tab
                        key={tabButtonLabels[key].id}
                        id={`plp-search-tab-${key}`}
                        ref={(el) => (tabRefs.current[key] = el)}
                        {...styles.searchSectionButton}
                        _focusVisible={{boxShadow: 'unset'}}
                        isDisabled={!isSearch}
                    >
                        <Flex {...styles.searchSectionButtonContent}>
                            <Text id={`plp-search-tab-${key}-label`}>{label}</Text>
                            <Text
                                id={`plp-search-tab-${key}-count`}
                                {...styles.searchSectionButtonCount}
                            >
                                {total}
                            </Text>
                        </Flex>
                    </Tab>
                )
            })}
        </TabList>
    )
}

SearchCategoryTabs.propTypes = {
    productSearchResult: PropTypes.object,
    tabButtonLabels: PropTypes.object,
    faqArticlesHits: PropTypes.number,
    manualsHits: PropTypes.number,
    isSearch: PropTypes.bool,
    selectedIndex: PropTypes.number,
}

export default SearchCategoryTabs
